import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Flame, Calendar } from "lucide-react";
import { StreakBanner } from "../components/StreakBanner";
import { QuizOptions } from "../components/QuizOptions";

function DailyFooter({ dc, result, user, chosen, onSubmit }) {
  if (result) {
    return (
      <div className="mt-4 rounded-md border border-white/10 bg-white/5 p-3 text-sm text-slate-300" data-testid="daily-explanation">
        <div className="font-semibold text-white">{result.is_correct ? "Nice! ✓" : "Not quite."}</div>
        <div className="mt-1 text-xs">{result.explanation}</div>
      </div>
    );
  }
  if (dc.completed) {
    return <div className="mt-6 rounded-md border border-lime-400/40 bg-lime-500/10 p-3 text-sm text-lime-200" data-testid="daily-completed">✓ You already answered today's challenge. Come back tomorrow!</div>;
  }
  if (!user) {
    return (
      <div className="mt-6 rounded-md border border-white/10 bg-white/5 p-3 text-xs text-slate-400">
        <Link to="/login" className="text-cyan-300 hover:underline">Sign in</Link> to save this answer to your streak.
      </div>
    );
  }
  return (
    <button onClick={onSubmit} disabled={chosen == null} data-testid="daily-submit" className="mt-6 inline-flex items-center gap-2 rounded-md bg-orange-500 px-5 py-3 text-sm font-semibold text-black hover:bg-orange-400 disabled:opacity-50">
      <Calendar size={14} /> Lock in answer
    </button>
  );
}

export default function DailyChallenge() {
  const { user } = useAuth();
  const [dc, setDc] = useState(null);
  const [chosen, setChosen] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/daily-challenge").then((r) => setDc(r.data)).catch((e) => setErr(apiError(e, "Could not load today's challenge.")));
  }, [user]);

  const submit = async () => {
    if (chosen == null || !user) return;
    try {
      const { data } = await api.post("/daily-challenge/attempt", { answer: chosen });
      setResult(data);
      const { data: fresh } = await api.get("/daily-challenge");
      setDc(fresh);
    } catch (e) {
      setErr(apiError(e, "Could not save your answer."));
    }
  };

  if (err) return <div className="rounded-md border border-red-400/40 bg-red-500/10 p-4 text-sm text-red-200" data-testid="daily-error">{err}</div>;
  if (!dc) return <div className="text-slate-400">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="daily-root">
      <div>
        <div className="inline-flex items-center gap-2 rounded-full border border-orange-400/30 bg-orange-500/10 px-3 py-1 text-xs uppercase tracking-widest text-orange-300">
          <Flame size={12} /> Daily Challenge · <span className="font-mono-num">{dc.date}</span>
        </div>
        <h1 className="font-display mt-4 text-3xl font-bold sm:text-4xl">One question. Keep your streak alive.</h1>
      </div>

      {user && <StreakBanner dc={dc} />}

      <div className="rounded-md border border-white/10 bg-black/40 p-6" data-testid="daily-question">
        <div className="text-[10px] uppercase tracking-widest text-cyan-300">Topic · {dc.topic_id}</div>
        <div className="font-display mt-2 text-lg font-semibold text-white">{dc.question.q}</div>
        <QuizOptions options={dc.question.options} chosen={chosen} correctIndex={result?.correct_index} graded={!!result} onChoose={setChosen} testId={(oi) => `daily-opt-${oi}`} />
        <DailyFooter dc={dc} result={result} user={user} chosen={chosen} onSubmit={submit} />
      </div>
    </div>
  );
}
